import { useSuspenseQuery } from "@tanstack/react-query";
import { createRoute, type AnyRoute } from "@tanstack/react-router";

import { loadView, viewQuery } from "@/api/views";
import { brandHead } from "@/lib/head";
import { toProposalPhase, toQnaTopic } from "@/modules/process-standard/ui/standards-search";
import StandardsPage from "@/pages/app/standards/page";
import ProposalPage from "@/pages/app/standards/proposals/[id]/page";
import ProposalsPage from "@/pages/app/standards/proposals/page";
import QuestionPage from "@/pages/app/standards/qna/[id]/page";
import QnaPage from "@/pages/app/standards/qna/page";

import { appRoute, APP_BOUNDARIES } from "./w0b";

// 주소의 쿼리는 사람이 고칠 수 있다 — 글자·숫자만 문자열로 받고 나머지는 버린다.
const textOf = (value: unknown): string | undefined =>
  typeof value === "string" ? value : typeof value === "number" ? String(value) : undefined;

const StandardsRouteComponent = () => {
  const { data } = useSuspenseQuery(viewQuery("standards"));
  return <StandardsPage data={data} />;
};

export const standardsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/standards",
  loader: ({ context }) => loadView(context.queryClient, viewQuery("standards")),
  head: () => brandHead("공정 표준"),
  component: StandardsRouteComponent,
  ...APP_BOUNDARIES,
});

// 제안 상태 거르기는 화면이 한다 — 「마감」 은 저장 상태가 아니라 전부 읽는다.
const ProposalsRouteComponent = () => {
  const { status } = standardProposalsRoute.useSearch();
  const { data } = useSuspenseQuery(viewQuery("standard-proposals", {}));
  return <ProposalsPage data={data} phase={toProposalPhase(status)} />;
};

export const standardProposalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/standards/proposals",
  validateSearch: (search: Record<string, unknown>): { status?: string } => ({ status: textOf(search.status) }),
  loader: ({ context }) => loadView(context.queryClient, viewQuery("standard-proposals", {})),
  head: () => brandHead("숫자 제안 · 공정 표준"),
  component: ProposalsRouteComponent,
  ...APP_BOUNDARIES,
});

const ProposalRouteComponent = () => {
  const { id } = standardProposalRoute.useParams();
  const { data } = useSuspenseQuery(viewQuery("standard-proposal", { id }));
  return <ProposalPage data={data} />;
};

export const standardProposalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/standards/proposals/$id",
  loader: ({ context, params }) => loadView(context.queryClient, viewQuery("standard-proposal", { id: params.id })),
  head: () => brandHead("숫자 제안 · 공정 표준"),
  component: ProposalRouteComponent,
  ...APP_BOUNDARIES,
});

const QnaRouteComponent = () => {
  const { topic } = standardQnaRoute.useSearch();
  const { data } = useSuspenseQuery(viewQuery("qna-feed", { topic: toQnaTopic(topic) }));
  return <QnaPage data={data} />;
};

export const standardQnaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/standards/qna",
  validateSearch: (search: Record<string, unknown>): { topic?: string } => ({ topic: textOf(search.topic) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => loadView(context.queryClient, viewQuery("qna-feed", { topic: toQnaTopic(deps.topic) })),
  head: () => brandHead("Q&A · 공정 표준"),
  component: QnaRouteComponent,
  ...APP_BOUNDARIES,
});

const QuestionRouteComponent = () => {
  const { id } = standardQuestionRoute.useParams();
  const { data } = useSuspenseQuery(viewQuery("qna-question", { id }));
  return <QuestionPage data={data} />;
};

export const standardQuestionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/standards/qna/$id",
  loader: ({ context, params }) => loadView(context.queryClient, viewQuery("qna-question", { id: params.id })),
  head: () => brandHead("Q&A · 공정 표준"),
  component: QuestionRouteComponent,
  ...APP_BOUNDARIES,
});

// g5a 배치의 라우트 — 공정 표준 열람·시공법 글 고치기 · 숫자 제안 목록·상세 · Q&A 피드·질문.
export const routes: AnyRoute[] = [standardsRoute, standardProposalsRoute, standardProposalRoute, standardQnaRoute, standardQuestionRoute];
